/* oxlint-disable max-lines -- legacy file, split it before you add code */
import { Button } from '@askable/ui/core/button';
import { DropdownMenuItem } from '@askable/ui/core/dropdown-menu';
import { Skeleton } from '@askable/ui/core/skeleton';
import { cn } from '@askable/ui/lib/utils';
import { Inbox } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useSubscription } from 'urql';

import { NotificationsItem } from './NotificationsItem';
import { MarkAllNotificationsAsReadByTeam } from './data/MarkAllNotificationsAsReadByTeam.mutation';
import { MarkNotificationAsRead } from './data/MarkNotificationAsRead.mutation';
import { NotificationsByTeam } from './data/NotificationsByTeam.query';
import { NotificationsByTeamSubscription } from './data/NotificationsByTeam.subscription';

import type { ResultOf } from 'src/graphql';

const MenuLabel = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div className={cn('px-3 pb-1 pt-2 text-xs font-semibold', className)}>{children}</div>
);

export type NotificationType = NonNullable<
  NonNullable<ResultOf<typeof NotificationsByTeam>['notificationsByTeam']>[number]
>;

type NotificationsContentProps = {
  teamId: string;
};

const LIMIT = 20;

const useNotificationsData = (teamId: string) => {
  const [allNotifications, setAllNotifications] = useState<NotificationType[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isMarkingAllAsRead, setIsMarkingAllAsRead] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [previousNotificationCount, setPreviousNotificationCount] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const markAllSpinnerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (markAllSpinnerTimer.current) {
        clearTimeout(markAllSpinnerTimer.current);
      }
    };
  }, []);

  const [{ data, fetching, error }] = useQuery({
    query: NotificationsByTeam,
    variables: {
      _team_id: teamId,
      offset: 0,
      limit: LIMIT,
    },
    requestPolicy: 'network-only',
    pause: !teamId,
  });

  const [loadMoreOffset, setLoadMoreOffset] = useState(0);

  const [loadMoreResult, executeLoadMore] = useQuery({
    query: NotificationsByTeam,
    variables: {
      _team_id: teamId,
      offset: loadMoreOffset,
      limit: LIMIT,
    },
    requestPolicy: 'network-only',
    pause: true,
  });

  const [, markNotificationAsRead] = useMutation(MarkNotificationAsRead);
  const [, markAllNotificationsAsReadByTeam] = useMutation(MarkAllNotificationsAsReadByTeam);

  // Use subscription for real-time updates
  useSubscription(
    {
      query: NotificationsByTeamSubscription,
      pause: fetching || !teamId,
      variables: { _team_id: teamId },
    },
    (_previous, response) => {
      if (response.notificationsByTeam) {
        const newNotification = response.notificationsByTeam as NotificationType;
        // Check if notification already exists to avoid duplicates
        setAllNotifications(prev => {
          const exists = prev.some(n => n._id === newNotification._id);
          if (!exists) {
            return [newNotification, ...prev];
          }
          return prev;
        });
      }
      return response;
    },
  );

  const onMarkAllNotifications = useCallback(() => {
    // Trim back to the first page — re-rendering hundreds of paginated items
    // with read: true is the kind of work that froze this dropdown to begin with.
    setAllNotifications(prev => prev.slice(0, LIMIT).map(notification => ({ ...notification, read: true })));

    // Brief spinner just to confirm the click landed — not tied to the fetch.
    setIsMarkingAllAsRead(true);
    if (markAllSpinnerTimer.current) clearTimeout(markAllSpinnerTimer.current);
    markAllSpinnerTimer.current = setTimeout(() => setIsMarkingAllAsRead(false), 1000);

    // Fire-and-forget: the unread badge resyncs via UnreadNotificationsCountByTeamSubscription,
    // and the local state above already reflects the read state. Failures are tolerated
    // and the user can retry. The Notifications typename is unnormalized in graphcache
    // (see network/client.ts) so writing this bulk response is cheap.
    markAllNotificationsAsReadByTeam({ _team_id: teamId }).catch(() => {});
  }, [markAllNotificationsAsReadByTeam, teamId]);

  const onLoadMore = useCallback(() => {
    const newOffset = allNotifications.length;
    setPreviousNotificationCount(newOffset);
    setLoadMoreOffset(newOffset);
    setIsLoadingMore(true);
  }, [allNotifications.length]);

  const onClickNotification = useCallback(
    (id: string | number) => {
      // Optimistic + fire-and-forget: update read state locally and let the
      // mutation run; failures are tolerated (user can retry / refresh).
      setAllNotifications(prev =>
        prev.map(notification => (notification._id === String(id) ? { ...notification, read: true } : notification)),
      );
      markNotificationAsRead({ _id: String(id) }).catch(() => {});
    },
    [markNotificationAsRead],
  );

  // Sync local state with the initial query — also handles teamId changes
  // (urql refetches with new variables, this reseeds and clears pagination).
  useEffect(() => {
    if (!data?.notificationsByTeam) return;
    const items = data.notificationsByTeam as NotificationType[];
    setAllNotifications(items);
    setHasMore(items.length === LIMIT);
    setLoadMoreOffset(0);
    setIsLoadingMore(false);
  }, [data?.notificationsByTeam]);

  // Trigger load more query when offset changes
  useEffect(() => {
    if (loadMoreOffset > 0 && isLoadingMore) {
      executeLoadMore();
    }
  }, [loadMoreOffset, isLoadingMore, executeLoadMore]);

  // Handle load more results
  useEffect(() => {
    if (!loadMoreResult.data?.notificationsByTeam) return;
    const newNotifications = loadMoreResult.data.notificationsByTeam as NotificationType[];
    setHasMore(newNotifications.length === LIMIT);
    if (newNotifications.length > 0) {
      setAllNotifications(prev => [...prev, ...newNotifications]);
      setLoadMoreOffset(prev => prev + newNotifications.length);

      // Scroll to the first newly loaded notification
      setTimeout(() => {
        if (containerRef.current && previousNotificationCount > 0) {
          const firstNewNotification = containerRef.current.querySelector(
            `[data-notification-index="${previousNotificationCount}"]`,
          );
          if (firstNewNotification) {
            firstNewNotification.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }, 100);
    }
    setIsLoadingMore(false);
  }, [loadMoreResult.data?.notificationsByTeam, previousNotificationCount]);

  return {
    notifications: allNotifications,
    fetching,
    error,
    hasMore,
    isLoadingMore,
    isMarkingAllAsRead,
    containerRef,
    onMarkAllNotifications,
    onLoadMore,
    onClickNotification,
  };
};

export const DesktopNotificationsContent = ({ teamId }: NotificationsContentProps) => {
  const { t } = useTranslation();
  const {
    notifications,
    fetching,
    error,
    hasMore,
    isLoadingMore,
    isMarkingAllAsRead,
    containerRef,
    onMarkAllNotifications,
    onLoadMore,
    onClickNotification,
  } = useNotificationsData(teamId);

  return (
    <div
      ref={containerRef}
      className={cn('relative max-h-[30rem] min-h-80 overflow-auto sm:min-h-40', {
        'divide-y-0.5 divide-border': !fetching,
      })}
    >
      {error ? <MenuLabel className="py-8 text-sm text-destructive">{error.message}</MenuLabel> : null}

      {fetching ? (
        <>
          <MenuLabel>
            <Skeleton className="h-10 w-full" />
          </MenuLabel>
          <MenuLabel>
            <Skeleton className="h-10 w-full" />
          </MenuLabel>
          <MenuLabel>
            <Skeleton className="h-10 w-2/3" />
          </MenuLabel>
        </>
      ) : null}

      {!error && !fetching && notifications.length > 0 ? (
        <>
          <MenuLabel className="flex items-center gap-2 pb-0 pr-1 pt-2 text-sm sm:justify-between sm:pt-2">
            <div className="hidden shrink-0 sm:flex">
              <Inbox className="size-4" />
            </div>
            <Button
              className="text-muted-foreground hover:text-foreground focus:text-foreground"
              variant="ghost"
              size="sm"
              onClick={onMarkAllNotifications}
              isLoading={isMarkingAllAsRead}
              disabled={isMarkingAllAsRead}
            >
              {t('components.notifications.markAsRead')}
            </Button>
          </MenuLabel>

          {notifications.map((item, index) => (
            <DropdownMenuItem key={item._id} asChild>
              <NotificationsItem item={item} index={index} onClick={onClickNotification} />
            </DropdownMenuItem>
          ))}
        </>
      ) : null}

      {hasMore ? (
        <Button
          variant="ghost"
          className="w-full rounded-none border-none focus-visible:ring-0"
          disabled={isLoadingMore}
          onClick={onLoadMore}
        >
          {isLoadingMore ? t('global.loading') : t('global.loadMore')}
        </Button>
      ) : null}

      {!error && !fetching && notifications.length === 0 ? (
        <MenuLabel className="flex flex-col justify-center gap-2 pt-12 text-center text-sm text-muted-foreground">
          <Inbox className="m-auto h-6 w-6" />
          {t('components.notifications.empty')}
        </MenuLabel>
      ) : null}
    </div>
  );
};

export const MobileNotificationsContent = ({ teamId }: NotificationsContentProps) => {
  const { t } = useTranslation();
  const {
    notifications,
    fetching,
    error,
    hasMore,
    isLoadingMore,
    isMarkingAllAsRead,
    containerRef,
    onMarkAllNotifications,
    onLoadMore,
    onClickNotification,
  } = useNotificationsData(teamId);

  return (
    <div
      ref={containerRef}
      className={cn('relative max-h-[30rem] min-h-80 overflow-auto sm:min-h-40', {
        'divide-y-0.5 divide-border': !fetching,
      })}
    >
      {error ? <MenuLabel className="py-8 text-sm text-destructive">{error.message}</MenuLabel> : null}

      {fetching ? (
        <>
          <MenuLabel>
            <Skeleton className="h-10 w-full" />
          </MenuLabel>
          <MenuLabel>
            <Skeleton className="h-10 w-full" />
          </MenuLabel>
          <MenuLabel>
            <Skeleton className="h-10 w-2/3" />
          </MenuLabel>
        </>
      ) : null}

      {!error && !fetching && notifications.length > 0 ? (
        <>
          <MenuLabel className="flex items-center gap-2 pb-0 pr-1 pt-2 text-sm sm:justify-between sm:pt-2">
            <div className="hidden shrink-0 sm:flex">
              <Inbox className="size-4" />
            </div>
            <Button
              className="text-muted-foreground hover:text-foreground focus:text-foreground"
              variant="ghost"
              size="sm"
              onClick={onMarkAllNotifications}
              isLoading={isMarkingAllAsRead}
              disabled={isMarkingAllAsRead}
            >
              {t('components.notifications.markAsRead')}
            </Button>
          </MenuLabel>

          {notifications.map((item, index) => (
            <NotificationsItem key={item._id} item={item} index={index} onClick={onClickNotification} />
          ))}
        </>
      ) : null}

      {hasMore ? (
        <Button
          variant="ghost"
          className="w-full rounded-none border-none focus-visible:ring-0"
          disabled={isLoadingMore}
          onClick={onLoadMore}
        >
          {isLoadingMore ? t('global.loading') : t('global.loadMore')}
        </Button>
      ) : null}

      {!error && !fetching && notifications.length === 0 ? (
        <MenuLabel className="flex flex-col justify-center gap-2 pt-12 text-center text-sm text-muted-foreground">
          <Inbox className="m-auto h-6 w-6" />
          {t('components.notifications.empty')}
        </MenuLabel>
      ) : null}
    </div>
  );
};
